import { useMemo } from "react";
import {
  ContactAndTravelerInformationState,
  ContactAndTravelerInformationChildState,
} from "@apac/checkout";
import {
  getChildState,
  getNestedChildState,
  useCheckoutState,
} from "@hopper-b2b/checkout";

/**
 * RBC flight-specific hook to determine if traveler information is loading.
 * Includes the 'validating' state which runs combined services for:
 * - Passenger validation
 * - Trip pricing update
 * - Additional points purchase eligibility check
 *
 * This ensures users cannot proceed until all validation is complete and
 * pricing/eligibility is updated.
 */
export const useFlightTravelerLoading = () => {
  const [state] = useCheckoutState();
  const stateValue = state?.value;

  const isLoading = useMemo(() => {
    if (!stateValue) return false;

    const childState = getChildState(stateValue);
    const nestedChildState = getNestedChildState(stateValue);

    // Runtime type checks to ensure state structure matches expected types
    if (
      typeof childState !== "string" ||
      typeof nestedChildState !== "string"
    ) {
      return false;
    }

    return (
      childState === ContactAndTravelerInformationState.loading ||
      [
        ContactAndTravelerInformationChildState.add,
        ContactAndTravelerInformationChildState.delete,
        ContactAndTravelerInformationChildState.update,
        ContactAndTravelerInformationChildState.validating,
      ].includes(nestedChildState as ContactAndTravelerInformationChildState)
    );
  }, [stateValue]);

  return isLoading;
};
